import React, {createContext, useContext, useEffect, useState} from 'react'
import {ThemeModeComponent} from '../../../assets/ts/layout'
import {toAbsoluteUrl} from '../../../helpers'

export type ThemeModeType = 'dark' | 'light' | 'system'
export type LayoutWidthType = 'full' | 'fixed'
const systemMode = ThemeModeComponent.getSystemMode() as 'light' | 'dark'

type ThemeModeContextType = {
  mode: ThemeModeType
  menuMode: ThemeModeType
  layoutWidth: LayoutWidthType
  updateMode: (_mode: ThemeModeType) => void
  updateMenuMode: (_mode: ThemeModeType) => void
  updateLayoutWidth: (_layoutWidth: LayoutWidthType) => void
}

const themeModeSwitchHelper = (_mode: ThemeModeType) => {
  // change background image url
  const mode = _mode !== 'system' ? _mode : systemMode
  const imageUrl = '/media/patterns/header-bg' + (mode === 'light' ? '.jpg' : '-dark.png')
  document.body.style.backgroundImage = `url("${toAbsoluteUrl(imageUrl)}")`
}

const themeModeLSKey = 'kt_theme_mode_value'
const themeMenuModeLSKey = 'kt_theme_mode_menu'
const layoutWidthLSKey = 'kt_layout_width_mode'

const getThemeModeFromLocalStorage = (lsKey: string): ThemeModeType => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return 'light'
  }

  const data = window.localStorage.getItem(lsKey)
  if (!data) {
    return 'light'
  }

  if (data === 'light') {
    return 'light'
  }

  if (data === 'dark') {
    return 'dark'
  }

  return 'system'
}

const getLayoutWidthFromLocalStorage = (lsKey: string): LayoutWidthType => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return 'full'
  }

  const data = window.localStorage.getItem(lsKey)

  if (data === 'fixed') {
    return 'fixed'
  }

  return 'full'
}

const defaultMode = getThemeModeFromLocalStorage(themeModeLSKey)
const defaultMenuMode = getThemeModeFromLocalStorage(themeMenuModeLSKey)
const defaultLayoutWidth = getLayoutWidthFromLocalStorage(layoutWidthLSKey)

const ThemeModeContext = createContext<ThemeModeContextType>({
  mode: defaultMode,
  menuMode: defaultMenuMode,
  layoutWidth: defaultLayoutWidth,
  updateMode: (_mode: ThemeModeType) => {},
  updateMenuMode: (_menuMode: ThemeModeType) => {},
  updateLayoutWidth: (_layoutWidth: LayoutWidthType) => {},
})

const useThemeMode = () => useContext(ThemeModeContext)

const ThemeModeProvider = ({children}: {children: React.ReactNode}) => {
  const [mode, setMode] = useState<ThemeModeType>(defaultMode)
  const [menuMode, setMenuMode] = useState<ThemeModeType>(defaultMenuMode)
  const [layoutWidth, setLayoutWidth] = useState<LayoutWidthType>(defaultLayoutWidth)

  const updateMode = (_mode: ThemeModeType) => {
    const updatedMode = _mode === 'system' ? systemMode : _mode
    setMode(updatedMode)
    themeModeSwitchHelper(updatedMode)
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(themeModeLSKey, updatedMode)
    }
    document.documentElement.setAttribute('data-theme', updatedMode)
    ThemeModeComponent.init()
  }

  const updateMenuMode = (_menuMode: ThemeModeType) => {
    setMenuMode(_menuMode)
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(themeMenuModeLSKey, _menuMode)
    }
  }

  const updateLayoutWidth = (_layoutWidth: LayoutWidthType) => {
    setLayoutWidth(_layoutWidth)
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(layoutWidthLSKey, _layoutWidth)
    }
  }

  useEffect(() => {
    if (typeof document === 'undefined') {
      return
    }

    const {body} = document
    body.classList.remove('layout-full-width', 'layout-fixed-width')
    body.classList.add(layoutWidth === 'full' ? 'layout-full-width' : 'layout-fixed-width')

    if (layoutWidth !== 'full') {
      body.classList.remove('aside-hidden')
    }
  }, [layoutWidth])

  useEffect(() => {
    updateMode(mode)
    updateMenuMode(menuMode)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <ThemeModeContext.Provider
      value={{mode, menuMode, layoutWidth, updateMode, updateMenuMode, updateLayoutWidth}}
    >
      {children}
    </ThemeModeContext.Provider>
  )
}

export {ThemeModeProvider, useThemeMode, systemMode, themeModeSwitchHelper}
